import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';

interface FloatingParticipantStripProps {
  children: React.ReactNode;
  defaultCorner?: 'top-right' | 'bottom-right';
}

const FloatingParticipantStrip: React.FC<FloatingParticipantStripProps> = ({
  children,
  defaultCorner = 'top-right'
}) => {
  const stripRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

  const clampPosition = useCallback((x: number, y: number) => {
    const strip = stripRef.current;
    const margin = 8;
    const maxX = window.innerWidth - (strip?.offsetWidth ?? 168) - margin;
    const maxY = window.innerHeight - (strip?.offsetHeight ?? 200) - margin;
    return {
      x: Math.max(margin, Math.min(x, maxX)),
      y: Math.max(margin, Math.min(y, maxY))
    };
  }, []);

  useEffect(() => {
    const updateDefaultPosition = () => {
      const strip = stripRef.current;
      const width = strip?.offsetWidth ?? 168;
      const margin = 16;
      const topOffset = defaultCorner === 'top-right' ? 72 : window.innerHeight - (strip?.offsetHeight ?? 220) - 96;

      setPosition(clampPosition(
        window.innerWidth - width - margin,
        topOffset
      ));
    };

    updateDefaultPosition();
    window.addEventListener('resize', updateDefaultPosition);
    return () => window.removeEventListener('resize', updateDefaultPosition);
  }, [clampPosition, defaultCorner]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!position) return;
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      origX: position.x,
      origY: position.y
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) return;
    const nextX = dragStateRef.current.origX + (event.clientX - dragStateRef.current.startX);
    const nextY = dragStateRef.current.origY + (event.clientY - dragStateRef.current.startY);
    setPosition(clampPosition(nextX, nextY));
  };

  const handlePointerUp = () => {
    dragStateRef.current = null;
  };

  if (!position) return null;

  return (
    <div
      ref={stripRef}
      data-recording-strip=""
      className="absolute z-30 w-32 sm:w-44 max-h-[45vh] sm:max-h-[55vh] rounded-xl border border-white/15 bg-slate-900/90 backdrop-blur-md shadow-2xl overflow-hidden touch-none"
      style={{ left: position.x, top: position.y }}
    >
      <div
        className="flex items-center gap-2 px-2 py-1.5 border-b border-white/10 cursor-grab active:cursor-grabbing select-none bg-slate-800/80"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="text-xs text-gray-300 font-medium truncate">Katılımcılar</span>
      </div>
      <div className="p-2 flex flex-col gap-2 overflow-y-auto max-h-[calc(45vh-36px)] sm:max-h-[calc(55vh-36px)]">
        {children}
      </div>
    </div>
  );
};

export default FloatingParticipantStrip;
